/**
 * Learner attendance read (Phase SC-8).
 *
 * `GET /api/me/attendance` — today's mark, recent days, rolling summary.
 * Populated when the learner checks into a seat booking (which stamps
 * attendance server-side) or when staff marks them present.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api } from "../api";

export interface AttendanceRecord {
  id: string;
  date_key: string;
  status: string;
  mode_used: string;
  check_in_at: string | null;
  late_minutes: number;
  metadata: Record<string, unknown>;
}

export interface AttendanceSummary {
  student_id: string;
  total_days: number;
  present_days: number;
  absent_days: number;
  late_days: number;
  attendance_percentage: number;
  threshold_pct: number;
  meets_threshold: boolean;
}

export interface MyAttendance {
  student_id: string;
  today: AttendanceRecord | null;
  recent: AttendanceRecord[];
  summary: AttendanceSummary;
}

const MY_ATTENDANCE_KEY = ["my-attendance"] as const;

export function useMyAttendance(enabled = true): UseQueryResult<MyAttendance> {
  return useQuery<MyAttendance>({
    queryKey: MY_ATTENDANCE_KEY,
    queryFn: () => api.get<MyAttendance>("/api/me/attendance"),
    enabled,
    retry: false,
  });
}

export function attendanceStatusLabel(status: string): string {
  switch (status) {
    case "present":
      return "Present";
    case "late":
      return "Late";
    case "absent":
      return "Absent";
    case "excused":
      return "Excused";
    case "half_day":
      return "Half day";
    default:
      return status;
  }
}

export { MY_ATTENDANCE_KEY };
